/**
 * RecoveryService
 * ──────────────────────────────────────────────────────────────────────────────
 * Handles all logic for the manual account recovery system:
 *   - SHA-256 hashing (salted with userId for answers)
 *   - 4-digit PIN management
 *   - Security question save / verification
 *   - Public /recovery_index collection management
 */

import { getDbSafe, getAuthSafe } from './firebaseConfig';
import {
    doc, getDoc, updateDoc, setDoc, deleteDoc, collection, query, where, getDocs, limit,
} from 'firebase/firestore';

// ── Predefined question bank ──────────────────────────────────────────────────
export const SECURITY_QUESTIONS = [
    "What was the name of your first pet?",
    "What city were you born in?",
    "What is your mother's maiden name?",
    "What was the name of your primary school?",
    "What is the name of your oldest sibling?",
    "What was the make of your first car?",
    "What is your favourite childhood movie?",
    "What street did you grow up on?",
    "What was your childhood nickname?",
    "What is the name of your hometown?",
] as const;

// ── Crypto helpers ────────────────────────────────────────────────────────────

/**
 * Produces a hex-encoded SHA-256 digest of the given string.
 * Uses the browser's native SubtleCrypto API (available in all modern browsers
 * and in Capacitor's WKWebView / WebView).
 */
export async function hashValue(value: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(value);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Salted hash for security answers.
 * Formula: SHA256(answer + userId)
 * Ensures identical answers from different users produce different hashes.
 * Comparison is ALWAYS case-sensitive (as per spec).
 */
export async function hashAnswer(answer: string, userId: string): Promise<string> {
    return hashValue(answer + userId);
}

/**
 * Timing-safe comparison (constant time) for two hex strings.
 * Not strictly necessary for client-side code, but good hygiene.
 */
function safeEqual(a: string, b: string): boolean {
    if (a.length !== b.length) return false;
    let diff = 0;
    for (let i = 0; i < a.length; i++) {
        diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
    }
    return diff === 0;
}

// ── PIN management ────────────────────────────────────────────────────────────

/** Returns true if the user already has a recovery PIN set. */
export async function hasPIN(userId: string): Promise<boolean> {
    try {
        const db = await getDbSafe();
        const userDoc = await getDoc(doc(db, 'users', userId));
        return !!(userDoc.data()?.recovery_pin);
    } catch {
        return false;
    }
}

/** Hashes and saves a 4-digit PIN to the user's Firestore document. */
export async function setPIN(userId: string, pin: string): Promise<void> {
    if (!/^\d{4}$/.test(pin)) throw new Error('PIN must be exactly 4 digits.');
    const db = await getDbSafe();
    const pinHash = await hashValue(pin);
    await updateDoc(doc(db, 'users', userId), { recovery_pin: pinHash });
}

/** Returns true if the supplied PIN matches the stored hash. */
export async function verifyPIN(userId: string, pin: string): Promise<boolean> {
    try {
        const db = await getDbSafe();
        const userDoc = await getDoc(doc(db, 'users', userId));
        const storedHash = userDoc.data()?.recovery_pin as string | undefined;
        if (!storedHash) return false;
        const inputHash = await hashValue(pin);
        return safeEqual(inputHash, storedHash);
    } catch {
        return false;
    }
}

// ── Security questions ────────────────────────────────────────────────────────

export interface SecurityQuestionInput {
    question: string;
    answer: string; // Plain text — will be hashed before storage
}

/**
 * Hashes each answer (salted with userId) and persists the questions array
 * to the user's Firestore document AND the public /recovery_index collection.
 */
export async function setSecurityQuestions(
    userId: string,
    username: string,
    questions: SecurityQuestionInput[],
): Promise<void> {
    return setSecurityQuestionsWithExisting(userId, username, questions);
}

const KEEP_HASH_PREFIX = '__KEEP_HASH__';

/**
 * Like setSecurityQuestions, but supports a "keep existing hash" sentinel.
 * If an answer starts with __KEEP_HASH__, the suffix IS the already-hashed value
 * and is stored directly without re-hashing. This lets users update only the
 * questions they want to change without needing to re-enter unchanged answers.
 */
export async function setSecurityQuestionsWithExisting(
    userId: string,
    username: string,
    questions: SecurityQuestionInput[],
): Promise<void> {
    if (questions.length < 1) throw new Error('At least one security question is required.');

    const db = await getDbSafe();

    // Hash answers — unless they carry the sentinel prefix (pre-hashed passthrough)
    const hashed = await Promise.all(
        questions.map(async (q) => {
            const answerHash = q.answer.startsWith(KEEP_HASH_PREFIX)
                ? q.answer.slice(KEEP_HASH_PREFIX.length) // already hashed
                : await hashAnswer(q.answer, userId);
            return { question: q.question, answer_hash: answerHash };
        }),
    );

    // Save to user document (private)
    await updateDoc(doc(db, 'users', userId), { security_questions: hashed });

    // Update the public recovery_index with the FIRST question only
    const cleanUsername = username.replace('@', '').toLowerCase();
    await setDoc(doc(db, 'recovery_index', cleanUsername), {
        userId,
        question: hashed[0].question,
    });
}

/**
 * Removes the public recovery_index entry when the user clears their questions.
 */
export async function clearRecoveryIndex(username: string): Promise<void> {
    try {
        const db = await getDbSafe();
        const cleanUsername = username.replace('@', '').toLowerCase();
        await deleteDoc(doc(db, 'recovery_index', cleanUsername));
    } catch {
        // Non-fatal — index may not exist
    }
}

// ── Recovery lookup (used on LoginScreen) ────────────────────────────────────

export interface RecoveryLookupResult {
    userId: string;
    userEmail: string;
    question: string;
}

/**
 * Looks up a user by username and returns their userId, email, and assigned
 * security question from the public /recovery_index collection.
 * Returns null if the user has not set up recovery.
 */
export async function getRecoveryQuestion(username: string): Promise<RecoveryLookupResult | null> {
    try {
        const db = await getDbSafe();
        const cleanUsername = username.replace('@', '').toLowerCase();

        // 1. Read public recovery index
        const indexDoc = await getDoc(doc(db, 'recovery_index', cleanUsername));
        if (!indexDoc.exists()) return null;

        const { userId, question } = indexDoc.data() as { userId: string; question: string };
        if (!userId || !question) return null;

        // 2. Fetch the user's email from the users collection (needed for sendPasswordResetEmail)
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (!userDoc.exists()) return null;

        const userEmail = userDoc.data()?.email as string | undefined;
        if (!userEmail) return null;

        return { userId, userEmail, question };
    } catch (err) {
        console.error('[RecoveryService] getRecoveryQuestion failed:', err);
        return null;
    }
}

/**
 * Verifies the user's answer to their first security question.
 * Comparison is CASE-SENSITIVE (as per spec).
 * Salts with userId before hashing.
 */
export async function verifySecurityAnswer(
    userId: string,
    answer: string,
): Promise<boolean> {
    try {
        const db = await getDbSafe();
        const userDoc = await getDoc(doc(db, 'users', userId));
        const questions = userDoc.data()?.security_questions as Array<{ question: string; answer_hash: string }> | undefined;

        if (!questions || questions.length === 0) return false;

        const inputHash = await hashAnswer(answer, userId);
        return safeEqual(inputHash, questions[0].answer_hash);
    } catch {
        return false;
    }
}

/**
 * Returns true if the user has both a PIN and at least one security question set.
 */
export async function hasRecoverySetup(userId: string): Promise<boolean> {
    try {
        const db = await getDbSafe();
        const userDoc = await getDoc(doc(db, 'users', userId));
        const data = userDoc.data();
        return !!(data?.recovery_pin && data?.security_questions?.length > 0);
    } catch {
        return false;
    }
}
